"use client";

import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import {
  Settings2,
  BrainCircuit,
  History,
  Smartphone,
  Coins,
  Mic,
  Camera,
  FileText,
} from "lucide-react";

interface FeatureConfig {
  key: string;
  icon: typeof Settings2;
  gradient: string;
  glowColor: string;
  bgAccent: string;
  iconColor: string;
  comingSoon?: boolean;
}

const features: FeatureConfig[] = [
  {
    key: "context",
    icon: Settings2,
    gradient: "from-violet-500 to-purple-600",
    glowColor: "group-hover:shadow-violet-500/25",
    bgAccent: "bg-violet-500/10 dark:bg-violet-500/15",
    iconColor: "text-violet-600 dark:text-violet-400",
  },
  {
    key: "starkySense",
    icon: BrainCircuit,
    gradient: "from-blue-500 to-cyan-500",
    glowColor: "group-hover:shadow-blue-500/25",
    bgAccent: "bg-blue-500/10 dark:bg-blue-500/15",
    iconColor: "text-blue-600 dark:text-blue-400",
  },
  {
    key: "history",
    icon: History,
    gradient: "from-emerald-500 to-teal-500",
    glowColor: "group-hover:shadow-emerald-500/25",
    bgAccent: "bg-emerald-500/10 dark:bg-emerald-500/15",
    iconColor: "text-emerald-600 dark:text-emerald-400",
  },
  {
    key: "pwa",
    icon: Smartphone,
    gradient: "from-orange-500 to-amber-500",
    glowColor: "group-hover:shadow-orange-500/25",
    bgAccent: "bg-orange-500/10 dark:bg-orange-500/15",
    iconColor: "text-orange-600 dark:text-orange-400",
  },
  {
    key: "pricing",
    icon: Coins,
    gradient: "from-pink-500 to-rose-500",
    glowColor: "group-hover:shadow-pink-500/25",
    bgAccent: "bg-pink-500/10 dark:bg-pink-500/15",
    iconColor: "text-pink-600 dark:text-pink-400",
  },
  {
    key: "speech",
    icon: Mic,
    gradient: "from-indigo-500 to-blue-600",
    glowColor: "group-hover:shadow-indigo-500/25",
    bgAccent: "bg-indigo-500/10 dark:bg-indigo-500/15",
    iconColor: "text-indigo-600 dark:text-indigo-400",
  },
  {
    key: "imageTranslate",
    icon: Camera,
    gradient: "from-teal-500 to-emerald-500",
    glowColor: "group-hover:shadow-teal-500/25",
    bgAccent: "bg-teal-500/10 dark:bg-teal-500/15",
    iconColor: "text-teal-600 dark:text-teal-400",
  },
  {
    key: "pdf",
    icon: FileText,
    gradient: "from-slate-400 to-slate-500",
    glowColor: "group-hover:shadow-slate-500/20",
    bgAccent: "bg-slate-500/10 dark:bg-slate-500/15",
    iconColor: "text-slate-500 dark:text-slate-400",
    comingSoon: true,
  },
];

export function FeaturesSection() {
  const t = useTranslations("landing.featuresSection");

  return (
    <section className="px-4">
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            {t("title")}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t("subtitle")}
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.key}
                className={cn(
                  "group relative rounded-2xl border border-border/60 bg-card p-6",
                  "transition-all duration-300 ease-out",
                  "hover:-translate-y-1 hover:shadow-xl",
                  feature.glowColor,
                  feature.comingSoon && "opacity-75"
                )}
              >
                {/* Gradient top accent line */}
                <div
                  className={cn(
                    "absolute inset-x-5 top-0 h-[2px] rounded-t-2xl bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-opacity duration-300",
                    feature.gradient
                  )}
                />

                {/* Icon */}
                <div
                  className={cn(
                    "w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110",
                    feature.bgAccent
                  )}
                >
                  <Icon className={cn("w-5 h-5", feature.iconColor)} />
                </div>

                {/* Title + Coming Soon badge */}
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-foreground leading-tight">
                    {t(`${feature.key}.title`)}
                  </h3>
                  {feature.comingSoon && (
                    <span className="shrink-0 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border/50">
                      {t("comingSoonBadge")}
                    </span>
                  )}
                </div>

                {/* Description */}
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {t(`${feature.key}.description`)}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
